import React from 'react';
import { ViewMode } from './scopeUtilsLib';
import { ConfigurationFieldOwner, ConfigurationRuleSetupType } from '@ydistri/api-sdk';
import { useFieldBounds } from '../../../apis/apiConfigurationFieldSpecifications';
import { ConfigurationInput, ConfigurationSwitch, OverwrittenIcon } from '@ydistri/ds';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import { Button } from 'antd';

interface ScopeConfigurationFieldProps {
  viewMode?: ViewMode;
  value: number | boolean | null | undefined;
  field: ConfigurationRuleSetupType;
  disabledFinal?: boolean;
  onChangeSwitchHandler?: () => void;
  onChangeInputHandler?: (newValue: string) => void;
  status?: ConfigurationFieldStatus;
  fieldIsOverwritten?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function -- we need empty handler here
const emptyChangeHandler = () => {};

const ScopeConfigurationField: React.FC<ScopeConfigurationFieldProps> = ({
  viewMode = ViewMode.GENERAL_CONFIGURATION,
  value,
  field,
  disabledFinal = false,
  onChangeSwitchHandler = emptyChangeHandler,
  onChangeInputHandler = emptyChangeHandler,
  status = ConfigurationFieldStatus.DEFAULT,
  fieldIsOverwritten = false,
}) => {
  // The same bounds the server refuses outside of, read once per session.
  const bounds = useFieldBounds(ConfigurationFieldOwner.ConfigurationRuleSetup, field);

  if (
    (viewMode === ViewMode.EXCEPTION || viewMode === ViewMode.NEW_EXCEPTION_MODAL) &&
    value === null
  ) {
    return null;
  }

  switch (field) {
    case ConfigurationRuleSetupType.UseAsSource:
    case ConfigurationRuleSetupType.UseAsTarget:
    case ConfigurationRuleSetupType.IsEnabled:
      return (
        <ConfigurationSwitch
          disabled={disabledFinal}
          status={status}
          overwritten={fieldIsOverwritten}
          overwrittenIcon={OverwrittenIcon.WARNING}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- value is boolean in these cases
          value={value as boolean}
          onChange={onChangeSwitchHandler}
          includeEmptySpace={false}
        />
      );
    case ConfigurationRuleSetupType.MaxNumberOfSourceStores:
    case ConfigurationRuleSetupType.MaxNumberOfTargetStores:
    case ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions:
    case ConfigurationRuleSetupType.MinimumRouteValue:
      // Editing waits for the bounds: without them a value the server refuses would go through.
      return (
        <>
          <ConfigurationInput
            disabled={disabledFinal || !bounds.ready}
            status={status}
            overwritten={fieldIsOverwritten}
            overwrittenIcon={OverwrittenIcon.WARNING}
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- value is number in these cases
            value={(value ?? 0) as number}
            onChange={onChangeInputHandler}
            placeholder="Unlimited"
            min={bounds.min}
            max={bounds.max}
            allowDecimals={bounds.allowDecimals}
          />
          {bounds.failed && (
            <Button type="link" size="small" danger onClick={bounds.retry}>
              Bounds unavailable - retry
            </Button>
          )}
        </>
      );
  }
};

export default ScopeConfigurationField;
